/* noted. — Skipstone allocation table: make the footer agree with the lines.

   OFF BY DEFAULT. Set window.SKIPSTONE_RECONCILE_FOOTER = true in
   assets/skipstone-tiers.js to arm it. Config, not code.

   ------------------------------------------------------------------
   WHY THIS EXISTS, AND WHY IT IS DANGEROUS.

   skipstone-portal-pricing.js prints the member price on every line. The
   footer beneath it is Awtomic's: a retail subtotal, a discount row, and a
   total. On 2026-08-24 those two stopped agreeing.

   The membership benefits config was corrected. The SubscriptionManualDiscount
   values stamped on the existing contracts were not, and Awtomic's footer
   totals read from the stamp. So the footer says the member saves $130 when
   the corrected config says $165, and the Total runs $35 high against the
   sum of the lines directly above it.

   Order #31276, billed 2026-08-21 off this same contract, charged three
   2024 Oliver's Blend 750ml at $225 each, the corrected $25 rate, while the
   stamp on that line reads $10 today. Applied as "Cave Collector", each-line,
   by the Memberships Discount Checkout function at checkout. That is the
   evidence that BILLING resolves against the live config and the stamp is
   display state that lags.

   IT IS ONE ORDER. That is why this file is off by default.

   THE RISK, STATED PLAINLY. Unarmed, the page shows high and charges low, and
   nobody writes in about paying less than the screen said. Armed, if the
   reading above is wrong and billing follows the stamp instead, the page shows
   low and charges high. That is the direction that generates letters on
   billing day. Do not arm this on a hunch. Arm it when the stamp has been
   shown to be inert, or leave it off and accept a total that runs high.

   ------------------------------------------------------------------
   IT RECONCILES AGAINST WHAT IS ON SCREEN, NOT AGAINST THE PRICE TABLE.

   Every figure is read back out of the rendered rows. Where a line carries a
   struck retail and a member price, both are taken from the cell. Where it
   carries one figure, that figure is used for both. So the footer can only
   ever say what the lines above it add up to, whatever the price table holds
   and whatever Awtomic decides to print tomorrow. There is no second source of
   truth to drift from.

   THE SUBTOTAL IS NOT TOUCHED. It is Awtomic's retail sum and it is already
   correct. Only the discount row and the Total are rewritten, and only when
   they disagree with the lines by more than a cent.

   SHIPPING IS CARRIED THROUGH, not assumed to be zero. It is read from its own
   row and added back into the Total.

   THE DISCOUNT ROW IS FOUND BY ITS LABEL MATCHING A TIER NAME from
   SKIPSTONE_TIERS. Never by position: Awtomic reorders these rows, and
   rewriting the wrong one would put a discount figure where shipping goes.

   NOTHING IS WRITTEN IF ANY FIGURE COMES BACK NaN. A blank total is a bad
   afternoon. A wrong one is a refund queue.

   Scoped to table.bundle-details-table and to rows carrying bab-child, so the
   billing history table, which shares a class, is never touched. That
   collision has already broken this page once.

   setTimeout, never requestAnimationFrame. rAF does not fire in a hidden tab
   and the debounce latches. Third file in this build to learn that. */
(function () {
  "use strict";

  if (window.location.pathname.indexOf("/tools/bundle-subscriptions") === -1) return;

  var MONEY = /^\s*\$\s*[\d,]+(\.\d{2})?\s*$/;

  function armed() {
    return window.SKIPSTONE_RECONCILE_FOOTER === true;
  }

  function fmt(n) {
    return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function num(text) {
    var v = parseFloat(String(text).replace(/[^0-9.\-]/g, ""));
    return isNaN(v) ? NaN : v;
  }

  function tierNames() {
    var T = window.SKIPSTONE_TIERS || {};
    var out = [];
    for (var id in T) {
      if (T.hasOwnProperty(id) && T[id] && T[id].name) out.push(String(T[id].name).toLowerCase());
    }
    return out;
  }

  /* Read one line back off the screen. Returns retail and member unit prices
     and the quantity, or null if the row is not a priced wine line. */
  function readLine(row) {
    var cells = Array.prototype.slice.call(row.cells);
    if (!cells.length) return null;

    var moneyIdx = [];
    cells.forEach(function (c, i) {
      var s = c.querySelector("s");
      if (s || MONEY.test(c.textContent || "")) moneyIdx.push(i);
    });
    if (moneyIdx.length < 1) return null;

    var unitCell = cells[moneyIdx[0]];
    var struck = unitCell.querySelector("s");

    var retail, member;
    if (struck) {
      retail = num(struck.textContent);
      /* everything in the cell that is not the struck figure */
      var rest = "";
      Array.prototype.forEach.call(unitCell.childNodes, function (n) {
        if (n !== struck) rest += n.textContent || "";
      });
      member = num(rest);
    } else {
      if (!MONEY.test(unitCell.textContent || "")) return null;
      retail = member = num(unitCell.textContent);
    }

    var qty = NaN;
    if (moneyIdx[0] > 0) qty = parseInt(cells[moneyIdx[0] - 1].textContent, 10);
    if (isNaN(qty) || qty < 1) qty = 1;

    if (isNaN(retail) || isNaN(member)) return null;
    return { retail: retail, member: member, qty: qty };
  }

  function reconcile(table) {
    var body = table.tBodies && table.tBodies[0];
    var foot = table.tFoot;
    if (!body || !foot) return;

    var retail = 0, member = 0, counted = 0;
    Array.prototype.forEach.call(body.rows, function (row) {
      var cls = (row.className || "").toString();
      if (cls.indexOf("bab-child") === -1) return;
      var line = readLine(row);
      if (!line) return;
      retail += line.retail * line.qty;
      member += line.member * line.qty;
      counted++;
    });

    if (!counted) return;
    if (isNaN(retail) || isNaN(member)) return;
    if (member > retail + 0.005) return; /* nonsense; leave Awtomic's figures */

    var names = tierNames();
    var discountRow = null, totalRow = null, shipping = 0, shippingSeen = false;

    Array.prototype.forEach.call(foot.rows, function (row) {
      var label = (row.cells[0] ? row.cells[0].textContent : row.textContent) || "";
      var whole = (row.textContent || "").toLowerCase();
      var head = label.toLowerCase().trim();

      if (/^total\b/.test(head) || /^\s*total/.test(whole.slice(0, 8))) {
        if (!totalRow) totalRow = row;
        return;
      }
      if (head.indexOf("shipping") !== -1 || whole.indexOf("shipping") !== -1) {
        var s = num(lastMoney(row));
        if (!isNaN(s)) { shipping = s; shippingSeen = true; }
        return;
      }
      for (var i = 0; i < names.length; i++) {
        if (whole.indexOf(names[i]) !== -1) { discountRow = row; return; }
      }
    });

    if (!shippingSeen) shipping = 0;

    var discount = retail - member;
    var total = member + shipping;
    if (isNaN(discount) || isNaN(total)) return;

    if (discountRow) {
      var dCell = lastMoneyCell(discountRow);
      if (dCell) {
        var want = discount > 0.005 ? "-" + fmt(discount) : fmt(0);
        if (normalize(dCell.textContent) !== normalize(want)) dCell.textContent = want;
      }
    }
    if (totalRow) {
      var tCell = lastMoneyCell(totalRow);
      if (tCell && Math.abs(num(tCell.textContent) - total) > 0.005) {
        tCell.textContent = fmt(total);
      }
    }
  }

  function normalize(s) {
    return String(s).replace(/[\s−]/g, "").replace(/^-/, "-");
  }

  function lastMoneyCell(row) {
    var cells = Array.prototype.slice.call(row.cells);
    for (var i = cells.length - 1; i >= 0; i--) {
      if (/\$/.test(cells[i].textContent || "")) return cells[i];
    }
    return null;
  }

  function lastMoney(row) {
    var c = lastMoneyCell(row);
    return c ? c.textContent : "";
  }

  function run() {
    if (!armed()) return;
    Array.prototype.forEach.call(
      document.querySelectorAll("table.bundle-details-table"),
      reconcile
    );
  }

  function safeRun() {
    try { run(); } catch (e) { console.warn("[skipstone-footer-reconcile]", e); }
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    setTimeout(function () { queued = false; safeRun(); }, 80);
  }

  function start() {
    if (!armed()) return;
    try {
      safeRun();
      new MutationObserver(schedule).observe(document.body, {
        childList: true, subtree: true, characterData: true
      });
      [300, 800, 1500, 2200, 3500, 5000, 7000, 9500].forEach(function (ms) {
        setTimeout(safeRun, ms);
      });
    } catch (e) {
      console.warn("[skipstone-footer-reconcile] disabled", e);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
